"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsInsights = exports.googleAdsInsightsResultSchema = exports.googleAdsInsightsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var googleAdsInsightsInputSchema = zod_1.z
    .object({
    task: zod_1.z
        .string()
        .describe('Natural language description of what to analyze. Examples: "analyze my top campaigns this month", "find underperforming keywords", "review budget allocation across ad groups", "analyze Performance Max asset group performance"'),
    level: zod_1.z
        .enum(['campaign', 'adgroup', 'assetgroup', 'ad'])
        .nullish()
        .describe('Optional hint for what level to analyze. Agent can override based on task context. Options: campaign (campaign-level analysis), adgroup (ad group-level analysis), assetgroup (asset group-level analysis for Performance Max), ad (individual ad analysis)'),
    entityIds: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Optional specific campaign/adgroup/assetgroup/ad IDs to analyze. If provided, only these entities will be fetched. e.g. ["1234567890", "0987654321"]'),
    startDate: zod_1.z
        .string()
        .nullish()
        .describe('Start date for analysis in YYYY-MM-DD format. e.g. 2025-01-01. If not provided, defaults to 30 days ago.'),
    endDate: zod_1.z
        .string()
        .nullish()
        .describe('End date for analysis in YYYY-MM-DD format. e.g. 2025-01-31. If not provided, defaults to today.'),
    maxItems: zod_1.z
        .number()
        .min(500)
        .default(500)
        .describe('Maximum number of entities to fetch. Unless specified, defaults to 500. Minimum is 500.'),
    focusArea: zod_1.z
        .enum(['performance', 'budget', 'keywords', 'creative', 'general'])
        .nullish()
        .describe('Focus area for insights: performance (CTR, CPC, Quality Score optimization), budget (spend efficiency, impression share), keywords (search terms, match types, quality score), creative (ad copy, extensions), or general (overall review). Unless specified, defaults to general.'),
})
    .describe('Parameters for AI-powered Google Ads performance insights and recommendations. Use for analysis, recommendations, performance reviews, optimization questions. Do not use for raw data export.');
exports.googleAdsInsightsArgsSchema = zod_1.z
    .object({
    args: googleAdsInsightsInputSchema,
    emitMode: common_1.emitModeSchema,
})
    .describe('Parameters for AI-powered Google Ads performance insights and recommendations. Use for analysis, recommendations, performance reviews, optimization questions. Do not use for raw data export.');
exports.googleAdsInsightsResultSchema = zod_1.z.object({
    summary: zod_1.z.string().describe('Overall summary of insights and recommendations'),
    insights: zod_1.z
        .array(zod_1.z.object({
        category: zod_1.z
            .string()
            .describe('Category of insight (e.g., Performance, Budget, Keywords, Creative)'),
        finding: zod_1.z.string().describe('Key finding or observation'),
        recommendation: zod_1.z.string().describe('Recommended action to improve'),
        priority: zod_1.z.enum(['high', 'medium', 'low']).describe('Priority level'),
        impact: zod_1.z.string().describe('Expected impact of implementing the recommendation'),
    }))
        .describe('Detailed insights and recommendations'),
    topPerformers: zod_1.z
        .array(zod_1.z.object({
        id: zod_1.z.string(),
        name: zod_1.z.string(),
        metric: zod_1.z.string(),
        value: zod_1.z.number(),
        description: zod_1.z.string(),
    }))
        .nullish()
        .describe('Top performing entities'),
    underperformers: zod_1.z
        .array(zod_1.z.object({
        id: zod_1.z.string(),
        name: zod_1.z.string(),
        metric: zod_1.z.string(),
        value: zod_1.z.number(),
        description: zod_1.z.string(),
    }))
        .nullish()
        .describe('Underperforming entities that need attention'),
    performanceData: zod_1.z
        .object({
        campaigns: zod_1.z.array(zod_1.z.any()).nullish().describe('Campaign performance data'),
        adgroups: zod_1.z.array(zod_1.z.any()).nullish().describe('Ad group performance data'),
        ads: zod_1.z.array(zod_1.z.any()).nullish().describe('Ad performance data'),
        assetgroups: zod_1.z.array(zod_1.z.any()).nullish().describe('Asset group performance data'),
        dateRange: zod_1.z
            .object({
            start: zod_1.z.string(),
            end: zod_1.z.string(),
        })
            .nullish()
            .describe('Date range for the performance data'),
    })
        .nullish()
        .describe('Raw performance data fetched during analysis'),
});
exports.googleAdsInsights = (0, common_1.createEmitModeToolMetadata)({
    id: 'googleAdsInsights',
    displayName: 'Google Ads Insights & Recommendations',
    displayDescription: 'Analyze Google Ads performance using AI-powered insights. Agent intelligently fetches relevant data (campaigns, ad groups, asset groups, or ads) based on your request, then provides comprehensive analysis with actionable recommendations for optimization.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsInsightsArgsSchema,
}, exports.googleAdsInsightsResultSchema);
